import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CircleDot, Archive, Radio, Crosshair, Star } from "lucide-react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { PlayerSearch } from "@/components/PlayerSearch";
import { divisionTheme } from "@/lib/divisionTheme";
import { isRichEmpty } from "@/components/RichText";
import { SeasonCountdown } from "@/components/SeasonCountdown";

const fmtReviewDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function DivisionCard({ d, previous }) {
  const theme = divisionTheme(d.color);
  const navigate = useNavigate();
  return (
    <Link
      to={`/divisions/${d.slug}`}
      data-testid={`division-link-${d.slug}`}
      style={previous ? theme.vars : { ...theme.vars, borderTopColor: "var(--div-accent)", borderTopWidth: "3px" }}
      className={`group bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 hover:shadow-lg transition-all ${previous ? "hover:border-[#1E63C4]" : "hover:border-[var(--div-accent)]"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className={`flex h-11 w-11 items-center justify-center rounded-full ${previous ? "bg-gray-700 text-white" : "bg-[var(--div-accent)] text-white"}`}>
          {previous ? <Archive className="h-5 w-5 text-white" /> : <CircleDot className="h-6 w-6" />}
        </span>
        {previous && d.season && (
          <span className="text-[11px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{d.season}</span>
        )}
        {!previous && d.week && (
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <SeasonCountdown week={d.week} testid={`card-countdown-${d.slug}`} />
            <span className="text-[11px] font-semibold uppercase tracking-wide bg-[var(--div-tint)] text-[var(--div-accent)] px-2 py-1 rounded-full whitespace-nowrap" data-testid={`card-week-${d.slug}`}>Wk {d.week.current} of {d.week.total}</span>
          </div>
        )}
      </div>
      <h3 className={`font-heading font-bold text-xl transition-colors ${previous ? "group-hover:text-[#1E63C4]" : "group-hover:text-[var(--div-accent)]"}`}>{d.name}</h3>
      {d.night && !previous && <p className="text-sm text-[#E11D2A] font-semibold mt-1">{d.night}</p>}
      <p className="text-sm text-[#555555] mt-2 leading-relaxed">{d.description}</p>
      {!previous && d.weekly_highlights_enabled !== false && (d.live_stream_teams || (d.show_wanted !== false && d.wanted_player)) && (
        <div className="mt-3 space-y-1">
          {d.live_stream_teams && (
            <p className="text-xs font-semibold text-[#E11D2A] flex items-center gap-1.5" data-testid={`card-live-stream-${d.slug}`}>
              <Radio className="h-3.5 w-3.5 shrink-0" /> Live Stream: <span className="font-medium text-[#333333]">{d.live_stream_teams}</span>
            </p>
          )}
          {d.show_wanted !== false && d.wanted_player && (
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5" data-testid={`card-wanted-${d.slug}`}>
              <Crosshair className="h-3.5 w-3.5 shrink-0" /> WANTED: <span className="font-medium text-[#333333]">{d.wanted_player}</span>
            </p>
          )}
        </div>
      )}
      <span className={`inline-flex items-center gap-1 font-semibold text-sm mt-4 group-hover:gap-2 transition-all ${previous ? "text-[#1E63C4]" : "text-[var(--div-accent)]"}`}>
        {previous ? "View final results" : "View division"} <ArrowRight className="h-4 w-4" />
      </span>
      {d.review && !isRichEmpty(d.review) && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/divisions/${d.slug}?view=review`); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); navigate(`/divisions/${d.slug}?view=review`); } }}
          data-testid={`review-tag-${d.slug}`}
          className="mt-3 ml-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--div-accent)] bg-[var(--div-tint)] border border-[var(--div-border)] rounded-full px-3 py-1 hover:brightness-95 transition-all align-middle"
        >
          <Star className="h-3.5 w-3.5 shrink-0" /> Review{fmtReviewDate(d.review_date) ? ` · ${fmtReviewDate(d.review_date)}` : ""}
        </span>
      )}
    </Link>
  );
}

export default function Divisions() {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("current");

  useEffect(() => {
    api.get("/divisions").then((r) => setDivisions(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const current = divisions.filter((d) => !d.archived);
  const previous = divisions.filter((d) => d.archived);
  const list = view === "current" ? current : previous;

  return (
    <Layout>
      <Seo title="Pool League Standings, Schedules & FargoRate Stats | Quinte BCA" description="Follow Quinte BCA league standings, schedules, team results, player statistics and current FargoRate information for active divisions." path="/divisions" />
      <PageHeader
        eyebrow="Standings • Stats • Schedules"
        title="Divisions"
        subtitle="Select a division to view its standings, player and team stats, and schedule."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <PlayerSearch />
        <div className="flex gap-2 mb-8 border-b border-[#E5E7EB]">
          <button
            onClick={() => setView("current")}
            data-testid="divisions-tab-current"
            className={`px-4 py-3 font-medium border-b-2 -mb-px transition-colors ${
              view === "current" ? "border-[#1E63C4] text-[#1E63C4]" : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            Current ({current.length})
          </button>
          <button
            onClick={() => setView("previous")}
            data-testid="divisions-tab-previous"
            className={`px-4 py-3 font-medium border-b-2 -mb-px transition-colors ${
              view === "previous" ? "border-[#1E63C4] text-[#1E63C4]" : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            Previous ({previous.length})
          </button>
        </div>

        {loading ? (
          <p className="text-[#555555]">Loading divisions…</p>
        ) : list.length === 0 ? (
          <p className="text-[#555555]">
            {view === "current" ? "No active divisions right now. Check back soon!" : "No past divisions yet — completed seasons will appear here."}
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="divisions-grid">
            {list.map((d) => (
              <DivisionCard key={d.id} d={d} previous={view === "previous"} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
